import {
	GroupedTimetableByVehicle,
	StopConnection,
	TransportTimetable,
	TransportVehicleKind,
} from '@/new-transport/_models/transport-timetable.model';
import { deepClone } from '@/_utils/deep-clone';
import { findStopConnection } from '@/new-transport/_utils/findStopConnection/find-stop-connection';
import { StopKind, Transport, TransportSide } from '@/new-transport/_models/transport.model';
import { removeDuplicatesFromArray } from '@/_utils/remove-duplicates-from-array';
import { roundToNearestFiveMinutes } from '@/new-transport/_utils/round-to-nearest-five-minutes/round-to-nearest-five-minutes';
import { MatSnackBar } from '@angular/material/snack-bar';
import { InfoboxService } from '@/common/infobox/infobox.service';

export type TimetableEntry = {
	timetables: TransportTimetable[];
	vehicleId: number;
};

export class TimetableCalculator {
	private readonly isDepartureSide: boolean;

	/**
	 * Initializes a new instance of the TimetableCalculator class.
	 * @param {GroupedTimetableByVehicle[]} groupedVehicles - The list of vehicles and their timetables.
	 * @param {StopConnection[]} stopsConnections - The connections between different stops.
	 * @param {Transport} transport - An object that stores transport information such as: side, name, id, etc.
	 * @param {MatSnackBar} _snackBar - A service for displaying snack bar messages to the user.
	 * @param {InfoboxService} infoboxService - Service to show some error messages to the user.
	 */
	constructor(
		private readonly groupedVehicles: GroupedTimetableByVehicle[],
		private readonly stopsConnections: StopConnection[],
		private readonly transport: Transport,
		private readonly _snackBar: MatSnackBar,
		private readonly infoboxService: InfoboxService,
	) {
		this.isDepartureSide = this.transport?.side === TransportSide.DEPARTURE;
	}

	/**
	 * Calculates and updates the related time departures for a specific vehicle and timetable.
	 * @param {number} vehicleId - The ID of the vehicle for which to calculate the timetable.
	 * @param {number} timetableId - The ID of the timetable to calculate.
	 * @param {string} timeDeparture - The new departure time for the specified timetable.
	 * @returns {TimetableEntry[]} The updated timetable entries.
	 */
	calculateRelatedTimeDepartures(
		vehicleId: number,
		timetableId: number,
		timeDeparture: string,
	): GroupedTimetableByVehicle[] {
		const groupedTimetablesToCalculate: TimetableEntry[] = this.processTimetablesToCalculateWithAntennas(
			timetableId,
			vehicleId,
		);

		const firstTimetable = this.findFirstTimetable(groupedTimetablesToCalculate, vehicleId, timetableId);

		if (firstTimetable) {
			firstTimetable.timeDeparture = timeDeparture;
		}

		const calculatedGroupedTimetables = this.mapTimetableEntryToGroupedVehicles(
			this.calculateTimetablesBaseOnStopConnections([...groupedTimetablesToCalculate], firstTimetable ?? null),
		);

		if (!calculatedGroupedTimetables) {
			return;
		}

		this._snackBar.open('Pomyślnie przeliczono godziny!', 'OK', { duration: 2000 });
		return calculatedGroupedTimetables;
	}

	/**
	 * Processes timetables to calculate the related timetables based on antennas (related vehicles).
	 * @param {number} timetableId - The ID of the timetable to start processing.
	 * @param {number} vehicleId - The ID of the vehicle to process.
	 * @returns {TimetableEntry[]} The timetables for the vehicle and its related antennas.
	 */
	processTimetablesToCalculateWithAntennas(timetableId: number, vehicleId: number): TimetableEntry[] {
		const groupedTimetablesToCalculate: TimetableEntry[] = [];

		const changedVehicle = this.groupedVehicles.find((v) => v.vehicle.id === vehicleId);
		if (changedVehicle) {
			this.createTimetablesToCalculate(changedVehicle, timetableId, groupedTimetablesToCalculate);
		}

		return removeDuplicatesFromArray(groupedTimetablesToCalculate);
	}

	/**
	 * Finds the first timetable for a specified vehicle and timetable ID.
	 *
	 * @param {TimetableEntry[]} groupedTimetables - Array of timetable entries grouped by vehicles.
	 * @param {number} vehicleId - The ID of the vehicle to find.
	 * @param {number} timetableId - The ID of the timetable to locate within the vehicle.
	 * @returns {TransportTimetable | null} - The found timetable object with stops, or `null` if not found.
	 */
	private findFirstTimetable(
		groupedTimetables: TimetableEntry[],
		vehicleId: number,
		timetableId: number,
	): TransportTimetable {
		return (
			groupedTimetables
				.find((group) => group.vehicleId === vehicleId)
				?.timetables.find((timetable) => timetable.id === timetableId) ?? null
		);
	}

	/**
	 * Adjusts a specified number of minutes to a given date.
	 * @param {Date} date - The date to adjust.
	 * @param {number} minutes - The number of minutes to adjust (can be positive to add or negative to subtract).
	 * @returns {Date} The new date after the minutes have been adjusted.
	 */
	private adjustMinutes(date: Date, minutes: number): Date {
		return new Date(date.getTime() + minutes * 60 * 1000);
	}

	/**
	 * Finds a timetable for a specific stop ID within a list of timetables.
	 * @param {TransportTimetable[]} timetables - The list of timetables to search through.
	 * @param {number} stopId - The ID of the stop to find.
	 * @returns {TransportTimetable | null} The timetable that matches the stop ID, or null if not found.
	 */
	private findRelatedTimetable(timetables: TransportTimetable[], stopId: number): TransportTimetable {
		return timetables.find((timetable) => timetable.stop.id === stopId) ?? null;
	}

	/**
	 * Creates a list of timetables to calculate based on the provided vehicle grouping and timetable ID.
	 * This method identifies relevant timetables within the specified vehicle group and adds related
	 * parent and child vehicles (antennas) if applicable.
	 *
	 * @param {GroupedTimetableByVehicle} groupedVehicle - The vehicle grouping containing the timetable information.
	 * @param {number} timetableId - The ID of the timetable that has changed, triggering recalculations.
	 * @param {TimetableEntry[]} groupedTimetablesToCalculate - The array to store timetables that need recalculation.
	 */
	private createTimetablesToCalculate(
		groupedVehicle: GroupedTimetableByVehicle,
		timetableId: number,
		groupedTimetablesToCalculate: TimetableEntry[],
	) {
		const changedTimetableIndex = groupedVehicle.timetable.findIndex((t) => t.id === timetableId);

		if (changedTimetableIndex === -1) {
			return;
		}

		const timetablesToCalculate = this.isDepartureSide
			? deepClone(groupedVehicle.timetable).slice(0, changedTimetableIndex + 1)
			: deepClone(groupedVehicle.timetable).slice(changedTimetableIndex);

		groupedTimetablesToCalculate.push({
			timetables: timetablesToCalculate,
			vehicleId: groupedVehicle.vehicle.id,
		});

		// Add related parent bus if vehicle is antenna and related stop has changed
		if (groupedVehicle.vehicle.transportVehicleKind === TransportVehicleKind.ANTENNA) {
			this.groupedVehicles.forEach((grouped) => {
				grouped.antennasForVehicle.forEach((antenna) => {
					const hasOverlappingStops = groupedTimetablesToCalculate
						.flatMap((v) => v.timetables.map((t) => t.stop.id))
						.some((id) => grouped.timetable.map((v) => v.stop.id).includes(id));

					if (
						antenna.vehicle !== groupedVehicle.vehicle.id ||
						groupedTimetablesToCalculate.map((v) => v.vehicleId).includes(grouped.vehicle.id) ||
						!hasOverlappingStops
					) {
						return;
					}
					const relatedTimetableId = grouped?.timetable.find((t) => t.stop.id === antenna.city)?.id;
					this.createTimetablesToCalculate(grouped, relatedTimetableId, groupedTimetablesToCalculate);
				});
			});
		}
		// Add all related vehicles (antennas) for calculation
		groupedVehicle.antennasForVehicle.forEach((antenna) => {
			if (groupedTimetablesToCalculate.map((v) => v.vehicleId).includes(antenna.vehicle)) {
				return;
			}

			const relatedTimetable = this.findRelatedTimetable(timetablesToCalculate, antenna.city);
			if (!relatedTimetable) {
				return;
			}

			const antennaVehicle = this.groupedVehicles.find((v) => v.vehicle.id === antenna.vehicle);
			const relatedTimetableId = antennaVehicle?.timetable.find((t) => t.stop.id === antenna.city)?.id;

			if (antennaVehicle && relatedTimetableId) {
				this.createTimetablesToCalculate(antennaVehicle, relatedTimetableId, groupedTimetablesToCalculate);
			}
		});
	}

	/**
	 * Calculates the timetables based on stop connections and updates the time departures.
	 * @param {TimetableEntry[]} groupedTimetables - The timetables to calculate.
	 * @param firstTimetable - The original timetable where timeDeparture has changed by user
	 * @returns {TimetableEntry[]} The updated timetables with calculated time departures.
	 */
	private calculateTimetablesBaseOnStopConnections(
		groupedTimetables: TimetableEntry[],
		firstTimetable: TransportTimetable,
	): TimetableEntry[] {
		groupedTimetables.forEach((group) => {
			const timetablesToCalculate = this.isDepartureSide
				? [...group.timetables].reverse()
				: [...group.timetables];

			if (!timetablesToCalculate?.length) {
				return;
			}

			if (timetablesToCalculate[0].stop.stopType === StopKind.TARGET) {
				return;
			}

			timetablesToCalculate.forEach((currentTimetable, index) => {
				if (index === 0) {
					if (firstTimetable?.stop?.id === currentTimetable.stop.id) {
						currentTimetable.timeDeparture = firstTimetable.timeDeparture;
						this.updateSameTimetablesAcrossAntennas(groupedTimetables, currentTimetable);
					}
					return;
				}

				if (currentTimetable.stop.stopType === StopKind.TARGET) {
					return;
				}

				const previousTimetable = timetablesToCalculate[index - 1];

				const [fromStopId, nextStopId] = this.isDepartureSide
					? [currentTimetable.stop.id, previousTimetable.stop.id]
					: [previousTimetable.stop.id, currentTimetable.stop.id];

				const stopConnection = findStopConnection(this.stopsConnections, fromStopId, nextStopId);

				if (!stopConnection) {
					const fromLocation = this.isDepartureSide
						? currentTimetable.stop.location.name
						: previousTimetable.stop.location.name;

					const toLocation = this.isDepartureSide
						? previousTimetable.stop.location.name
						: currentTimetable.stop.location.name;

					this.infoboxService.error(`
    					Błąd przy automatycznym przeliczaniu czasu! <br>
   						Brak czasu między przystankami dla: ${fromLocation} <i class="fas fa-arrow-right"></i> ${toLocation}
  					`);
					return;
				}

				const minutes = stopConnection.minutes + previousTimetable.pauseDuration;

				currentTimetable.timeDeparture = this.adjustMinutes(
					new Date(previousTimetable.timeDeparture),
					this.isDepartureSide ? -minutes : minutes,
				).toISOString();

				this.updateSameTimetablesAcrossAntennas(groupedTimetables, currentTimetable);
			});
		});

		return groupedTimetables;
	}

	/**
	 * Updates time departures for matching timetables across all related antennas (vehicles).
	 * @param {TimetableEntry[]} groupedTimetables - The grouped timetables that may need updates.
	 * @param {TransportTimetable} targetTimetable - The timetable with the updated departure time.
	 */
	private updateSameTimetablesAcrossAntennas(
		groupedTimetables: TimetableEntry[],
		targetTimetable: TransportTimetable,
	) {
		groupedTimetables.forEach(({ timetables }) => {
			timetables
				.filter(({ stop }) => stop.id === targetTimetable.stop.id)
				.forEach((matchingTimetable) => {
					matchingTimetable.timeDeparture = targetTimetable.timeDeparture;
				});
		});
	}

	/**
	 * Maps a list of timetable entries to their corresponding grouped vehicles, updating
	 * each timetable's departure time based on a specified rounding direction.
	 *
	 * @function mapTimetableEntryToGroupedVehicles
	 * @param {TimetableEntry[]} groupedRelatedTimeDepartures - Array of timetable entries that include time departure data for each vehicle.
	 * @returns {GroupedTimetableByVehicle[]} Array of grouped vehicles, each with an updated timetable where applicable.
	 */
	private mapTimetableEntryToGroupedVehicles(
		groupedRelatedTimeDepartures: TimetableEntry[],
	): GroupedTimetableByVehicle[] {
		return [
			...this.groupedVehicles.map((grouped) => {
				const groupedVehicle = groupedRelatedTimeDepartures.find((v) => v.vehicleId === grouped.vehicle.id);

				if (!groupedVehicle) {
					return grouped;
				}

				grouped.timetable.forEach((timetable) => {
					const timetableToChange = groupedVehicle.timetables.find((v) => v.id === timetable.id);

					if (timetableToChange) {
						timetable.timeDeparture = roundToNearestFiveMinutes(
							timetableToChange.timeDeparture,
							this.isDepartureSide ? 'down' : 'up',
						);
					}
				});

				return grouped;
			}),
		];
	}
}
